import React from "react";
import { Redirect, withRouter } from "react-router-dom";
import { OrgContext } from "./context/OrgContext";
import _ from "lodash";
import { DRAYAGE_MODULE_HIDDEN } from "./config/AppConfig";

/**
 * Routes that are accessible even when Drayage module is disabled
 */
const DRAYAGE_EXCEPTION_ROUTES = [
  "/configurations", // Allow access to configurations page to manage drayage settings
];

// Drayage routes that require the module to be enabled
const DRAYAGE_ROUTES = [
  "/loads",
  "/equipment",
  "/owners",
  "/types",
  "/loads/pending",
];

/**
 * Check if the current path is an exception route
 * @param {string} pathname - Current pathname
 * @returns {boolean} - True if the route is an exception
 */
const isExceptionRoute = (pathname) => {
  return DRAYAGE_EXCEPTION_ROUTES.some((route) => pathname.startsWith(route));
};

/**
 * Check if current path is a drayage route
 * @param {string} pathname - Current pathname
 * @returns {boolean} - True if route is a drayage route
 */
const isDrayageRoute = (pathname) => {
  return DRAYAGE_ROUTES.some((route) => pathname.startsWith(route));
};

/**
 * Helper function to check if Drayage module is enabled
 * @param {Object} orgSettings - Organization settings object
 * @returns {boolean} - True if Drayage module is enabled
 */
export const isDrayageEnabled = (orgSettings) => {
  if (DRAYAGE_MODULE_HIDDEN) return false;
  return orgSettings && _.get(orgSettings, "drayage_enabled", "false") === "true";
};

/**
 * DrayageRouteGuard - Protects Drayage routes when the module is disabled
 * Redirects to dashboard if drayage_enabled org setting is not "true"
 * Exception routes are accessible even when the module is disabled
 */
const DrayageRouteGuardInternal = ({ children, location }) => {
  const { orgSettings } = React.useContext(OrgContext);
  const hasNotifiedRef = React.useRef(false);

  // Allow access to exception routes even when Drayage is disabled
  if (isExceptionRoute(location.pathname)) {
    return children;
  }

  // Redirect to dashboard if Drayage module is disabled AND trying to access drayage route
  if (!isDrayageEnabled(orgSettings) && isDrayageRoute(location.pathname)) {
    // Notify other tabs to redirect (only once per session)
    if (!hasNotifiedRef.current) {
      try {
        new BroadcastChannel('drayage_disable_channel').postMessage({ action: 'redirect_to_dashboard' });
        hasNotifiedRef.current = true;
      } catch (e) {}
    }
    // Redirect to dashboard if Drayage module is disabled
    return <Redirect to="/dashboard" />;
  }

  // Reset notification flag when Drayage is enabled again
  hasNotifiedRef.current = false;
  return children;
};

export default withRouter(DrayageRouteGuardInternal);
